import { useState, useCallback, useRef, useEffect, type DragEvent, type RefObject } from 'react'
import { MessageBubble } from './MessageBubble'
import type { DisplayMessage } from '@/hooks/useAndrzej'

interface Props {
  messages: DisplayMessage[]
  isThinking: boolean
  welcomeMessage: string
  showWelcome: boolean
  messagesEndRef: RefObject<HTMLDivElement | null>
  onAddPhoto: (file: File) => void
}

export function MessageList({ messages, isThinking, welcomeMessage, showWelcome, messagesEndRef, onAddPhoto }: Props) {
  const [isDragging, setIsDragging] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const dragCounterRef = useRef(0)
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll position detection for FAB
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const handleScroll = () => {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
      setShowScrollButton(!isNearBottom)
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesEndRef])

  // Drag & drop handlers
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragCounterRef.current++
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => e.preventDefault(), [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragCounterRef.current = 0
    setIsDragging(false)
    const files = e.dataTransfer?.files
    if (!files) return
    for (const file of files) {
      if (file.type.startsWith('image/')) onAddPhoto(file)
    }
  }, [onAddPhoto])

  return (
    <div
      ref={listRef}
      className="relative flex-1 overflow-y-auto px-md py-sm space-y-1"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--frame) var(--paper-dark)' }}
    >
      {showWelcome && (
        <div className="text-center text-sm italic py-8 px-md text-ink-light">
          {welcomeMessage}
        </div>
      )}

      {messages.map(msg => <MessageBubble key={msg.id} message={msg} />)}

      {isThinking && (
        <div className="flex items-center gap-1 text-xs px-sm py-sm animate-[message-in_0.2s_ease-out] text-botanical">
          <span className="italic">Andrzej myśli</span>
          <span className="inline-flex gap-[2px]">
            <span className="animate-[typing-pulse_1.4s_infinite]">.</span>
            <span className="animate-[typing-pulse_1.4s_0.2s_infinite]">.</span>
            <span className="animate-[typing-pulse_1.4s_0.4s_infinite]">.</span>
          </span>
        </div>
      )}

      <div ref={messagesEndRef} />

      {/* Drag & drop overlay */}
      {isDragging && (
        <div className="absolute inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded-xl border-[3px] border-dashed border-botanical bg-botanical/5">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-botanical/10">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-botanical)" strokeWidth="2" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>
          <span className="text-sm font-medium text-botanical">Upuść zdjęcia tutaj</span>
          <span className="text-[0.65rem] text-frame">max 5 &middot; JPEG, PNG, WebP, HEIC</span>
        </div>
      )}

      {/* Scroll-to-bottom FAB */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="sticky bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-botanical text-paper border-none cursor-pointer shadow-md hover:brightness-110 transition-[filter]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <polyline points="19 12 12 19 5 12"/>
          </svg>
        </button>
      )}
    </div>
  )
}
